import React, { useMemo, useContext } from 'react';
import { useTranslation } from 'Shared/contexts/TranslationsContext';
import { GlobalHotKeys } from 'react-hotkeys';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Context
import ExpandNavbarContext from 'Shared/contexts/ExpandNavbarContext';

// Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import SearchModal from 'Shared/components/SearchModal';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

// Styles
import './SearchButton.scss';

const keyMap = Object.freeze({
  OPEN_SEARCH_MODAL: ['command+k', 'ctrl+k'],
});

const SearchButton = () => {
  const isExpanded = useContext(ExpandNavbarContext);
  const { tr } = useTranslation();

  const [isOpen, toggleIsOpen] = useToggle(false);

  const handlers = useMemo(
    () => ({
      OPEN_SEARCH_MODAL: event => {
        event.preventDefault();
        toggleIsOpen();
      },
    }),
    [toggleIsOpen]
  );

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleIsOpen();
    }
  };

  const baseClassName = 'SearchButton';

  return (
    <>
      <GlobalHotKeys keyMap={keyMap} handlers={handlers}>
        {isExpanded ? (
          <div className={baseClassName}>
            <WpTextField
              id="global-search-placeholder"
              label={tr('perform.side_navigation.search')}
              labelHidden
              disabled
              placeholder={tr('perform.side_navigation.search')}
              prefixIcon="search"
              value={tr('perform.side_navigation.search')}
            />
            <div
              className={`${baseClassName}__overlay`}
              onClick={() => toggleIsOpen()}
              role="button"
              tabIndex={0}
              onKeyDown={handleKeyPress}
            />
          </div>
        ) : (
          <WpTooltip placement="right">
            <WpTooltipTrigger>
              <li className="nav-item">
                <button
                  type="button"
                  className="nav-link d-flex align-items-center cursor-pointer"
                  aria-label={tr('perform.side_navigation.search')}
                  onClick={() => toggleIsOpen()}
                >
                  <div className="side-navigation-icon position-relative mx-auto">
                    <WpIcon name="search" />
                  </div>
                </button>
              </li>
            </WpTooltipTrigger>
            <WpTooltipContent>{tr('perform.side_navigation.search')}</WpTooltipContent>
          </WpTooltip>
        )}
        {isOpen && <SearchModal isOpen onOpenToggle={toggleIsOpen} />}
      </GlobalHotKeys>
    </>
  );
};

export default SearchButton;
